import React, {useCallback, useEffect, useRef, useState} from "react";
import {Popover} from "../popover";
import {ReactComponent as NoteBookIcon} from '../../../../assets/images/note-book-icon.svg'
import ObserversService from "../../../../core/services/observers";
import classnames from "classnames";
import {useNotebookConfig} from "../../../hooks/use-notebook-config";
import {Fade} from "@mui/material";


const safeSpaceWidth = 20;
const defaultMargin = 20;

/**
 * Returns the visible viewport bounds (accounts for mobile browser UI via visualViewport).
 * @return {{width: number, height: number, offsetTop: number, offsetLeft: number}}
 */
const getViewportBounds = () => {
    const visualViewport = window.visualViewport;
    if (visualViewport) {
        return {
            width: visualViewport.width,
            height: visualViewport.height,
            offsetTop: visualViewport.offsetTop,
            offsetLeft: visualViewport.offsetLeft,
        };
    }
    return {
        width: window.innerWidth,
        height: window.innerHeight,
        offsetTop: 0,
        offsetLeft: 0,
    };
};

/**
 * @param {MouseEvent | TouchEvent} e
 * @return {{x: number, y: number}}
 */
const getPointerCoords = (e) => {
    if (e.touches?.length) {
        return {x: e.touches[0].clientX, y: e.touches[0].clientY};
    }
    if (e.changedTouches?.length) {
        return {x: e.changedTouches[0].clientX, y: e.changedTouches[0].clientY};
    }
    return {x: e.clientX, y: e.clientY};
};

const FloatingActionButton = ({children}) => {
    const [dragging, setDragging] = useState(false);
    const {show} = useNotebookConfig();
    /** @type {React.MutableRefObject<HTMLDivElement | null>}*/
    const floatyRef = useRef(null);
    const onLeave = useRef(() => void 0);
    const offsetRef = useRef({x: 0, y: 0});
    const isTouchDragRef = useRef(false);

    /**
     * When the show state changes:
     * - if the show state is false, calls the onLeave callback
     */
    useEffect(() => {
        if (!show) {
            onLeave.current?.();
        }
    }, [show]);

    /**
     * Applies default bottom-right position within the visible viewport.
     */
    const refreshCoordinates = useCallback(() => {
        const floaty = floatyRef.current;
        if (!floaty) {
            return;
        }

        const bounds = getViewportBounds();
        const top = bounds.offsetTop + bounds.height - defaultMargin - floaty.clientHeight;
        const left = bounds.offsetLeft + bounds.width - defaultMargin - floaty.clientWidth;

        floaty.style.bottom = 'auto';
        floaty.style.right = 'auto';
        floaty.style.top = `${top}px`;
        floaty.style.left = `${left}px`;
    }, []);

    /**
     * Handles calculating the new co-ordinates of the floaty component on screen.
     * @param {MouseEvent | TouchEvent} e
     */
    const mouseMoveHandler = useCallback((e) => {
        const floaty = floatyRef.current;
        if (!floaty) {
            return;
        }

        if (isTouchDragRef.current && e.cancelable) {
            e.preventDefault();
        }

        setDragging(true);

        const {x, y} = getPointerCoords(e);
        const dx = x - offsetRef.current.x;
        const dy = y - offsetRef.current.y;
        const bounds = getViewportBounds();

        const minTop = bounds.offsetTop + safeSpaceWidth;
        const maxTop = bounds.offsetTop + bounds.height - safeSpaceWidth - floaty.clientHeight;
        const minLeft = bounds.offsetLeft + safeSpaceWidth;
        const maxLeft = bounds.offsetLeft + bounds.width - safeSpaceWidth - floaty.clientWidth;

        const top = Math.min(Math.max(floaty.offsetTop + dy, minTop), maxTop);
        const left = Math.min(Math.max(floaty.offsetLeft + dx, minLeft), maxLeft);

        floaty.style.bottom = 'auto';
        floaty.style.right = 'auto';
        floaty.style.top = `${top}px`;
        floaty.style.left = `${left}px`;

        offsetRef.current.x = x;
        offsetRef.current.y = y;
    }, []);

    /**
     * Removes drag listeners when movement stops.
     */
    const mouseUpHandler = useCallback(() => {
        isTouchDragRef.current = false;
        setTimeout(() => setDragging(false), 100);
        document.removeEventListener('mousemove', mouseMoveHandler);
        document.removeEventListener('mouseup', mouseUpHandler);
        document.removeEventListener('touchmove', mouseMoveHandler);
        document.removeEventListener('touchend', mouseUpHandler);
    }, [mouseMoveHandler]);

    /**
     * @param {MouseEvent<HTMLDivElement> | TouchEvent<HTMLDivElement>} e
     * @param {boolean} isTouch
     */
    const mouseDownHandler = useCallback((e, isTouch = false) => {
        const {x, y} = getPointerCoords(e);
        offsetRef.current.x = x;
        offsetRef.current.y = y;
        isTouchDragRef.current = isTouch;

        if (isTouch) {
            document.addEventListener('touchmove', mouseMoveHandler, {passive: false});
            document.addEventListener('touchend', mouseUpHandler);
            return;
        }
        document.addEventListener('mousemove', mouseMoveHandler);
        document.addEventListener('mouseup', mouseUpHandler);
    }, [mouseMoveHandler, mouseUpHandler]);

    /**
     * Keeps FAB anchored when layout or mobile browser UI changes.
     */
    useEffect(() => {
        const layout = document.querySelector('main');
        const observer = ObserversService.newResizeObserver(refreshCoordinates);
        ObserversService.observeResizeObserver(observer, layout);
        refreshCoordinates();

        const visualViewport = window.visualViewport;
        if (visualViewport) {
            visualViewport.addEventListener('resize', refreshCoordinates);
            visualViewport.addEventListener('scroll', refreshCoordinates);
        }
        window.addEventListener('resize', refreshCoordinates);
        window.addEventListener('orientationchange', refreshCoordinates);

        return () => {
            ObserversService.disconnectResizeObserver(observer);
            if (visualViewport) {
                visualViewport.removeEventListener('resize', refreshCoordinates);
                visualViewport.removeEventListener('scroll', refreshCoordinates);
            }
            window.removeEventListener('resize', refreshCoordinates);
            window.removeEventListener('orientationchange', refreshCoordinates);
        };
    }, [refreshCoordinates]);

    /**
     * Re-anchor when FAB becomes visible again (e.g. after customer profile load).
     */
    useEffect(() => {
        if (show) {
            refreshCoordinates();
        }
    }, [show, refreshCoordinates]);

    return (
        <>
            <Popover
                classes={{
                    paper: 'floaty-popover-paper'
                }}
                elevation={0}
                marginThreshold={16}
                anchorOrigin={{
                    vertical: 'top',
                    horizontal: 'center',
                }}
                transformOrigin={{
                    vertical: 'bottom',
                    horizontal: 'right',
                }}
                child={(onEnter, onLeave) => {
                    onLeave.current = onLeave;
                    return (
                        <Fade in={show}>
                            <div
                                ref={floatyRef}
                                onMouseDown={(e) => mouseDownHandler(e)}
                                onTouchStart={(e) => mouseDownHandler(e, true)}
                                className={classnames('app-floaty-component', {show: show})}
                                onClick={(e) => !dragging && onEnter(e)}
                            >
                                <div className={classnames('cursor-pointer-hover', {'cursor-dragging': dragging})}>
                                    <NoteBookIcon/>
                                </div>
                            </div>
                        </Fade>
                    )
                }}
                popoverChild={() => children}
            />
        </>
    );
}

export default FloatingActionButton;
